<script setup>
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import AuthApi from '@/api/Auth';
import { userAgreed } from '@/api/UserInfo';
import { getTermsAndPoliciesAPI } from '@/api/Join';
import { Consent } from '@/components/auth';
import SmartwelSubscriptionScreen from '@/components/partner/SmartwelSubscriptionScreen.vue';
import SmartwelPaymentSheet from '@/components/partner/SmartwelPaymentSheet.vue';
import SmartwelSubscribeCompleteSheet from '@/components/partner/SmartwelSubscribeCompleteSheet.vue';
import SmartwelGuideScreen from '@/components/partner/SmartwelGuideScreen.vue';
import { useRnBridge } from '@/composables/useRnBridge';
import { useScrollLock } from '@/composables/useScrollLock';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { usePrimarySajuStore } from '@/store/modules/primarySaju';
import { DEV_MODE } from '@/config/dev-mode';
import { getPartnerMeta } from '@/constants/partner';
import smartwelLogo from '@/assets/partner/smartwel/smartwel-wordmark__black.png';
import smartwelLogoWhite from '@/assets/partner/smartwel/smartwel-wordmark__white.png';
import nangmanLogo from '@manseImages/logo_b.png';
import closeIcon from '@/assets/images/header-icons/close.png';

definePage({ meta: { layout: 'empty', public: true } });

const route = useRoute();
const router = useRouter();
const store = useStore();
const myInfoStore = useMyInfoStore();
const primarySajuStore = usePrimarySajuStore();
const { sendMessageToApp } = useRnBridge();

const step = ref('loading'); // 'loading' | 'error' | 'prompt' | 'done'(가입/연동 완료 → 진입 화면 언마운트)
const errorTitle = ref('연결에 실패했습니다');
const errorMessage = ref('');
const linking = ref(false);
const autoLinkPending = ref(false); // login-prompt에서 로그인 트리거 후 isLoggedIn 변화 감지용
const isInRnWebView = !!window.ReactNativeWebView;

// 이미 로그인된 사용자인지 감지(딥링크 진입 분기에 사용)
const isLoggedIn = computed(() => !!store.state.user?.token);
const currentUserName = computed(() => store.state.user?.name || store.state.user?.id || '');

// 약관 동의 상태 (필수 2 + 선택 1)
const agreedClause = ref(false); // [필수] 서비스 이용약관
const agreedPersonal = ref(false); // [필수] 개인정보 수집·이용
const agreedMarketing = ref(false); // [선택] 이벤트·혜택 알림 (마케팅 수신 동의)
const submitting = ref(false);

// 약관 본문(서버에서 로딩)
const clauseContent = ref('');
const personalContent = ref('');
const marketingContent = ref('');

// 약관 보기 팝업
const showConsent = ref(false);
const consentHeader = ref('');
const consentContent = ref('');

// 완료 팝업 / 나가기 팝업 / 약관 모달
const showExitModal = ref(false);
const showTermsModal = ref(false);
const completeShake = ref(false);
const exitShake = ref(false);
const termsShake = ref(false);
// 완료 팝업이 어떤 가입 경로로 떴는지: 'join'(복지찬스 신규) | 'link'(낭만 계정 연동)
const completeKind = ref('join');

const triggerShake = (which) => {
  if (which === 'complete') {
    completeShake.value = false;
    requestAnimationFrame(() => {
      completeShake.value = true;
      setTimeout(() => (completeShake.value = false), 400);
    });
  } else if (which === 'exit') {
    exitShake.value = false;
    requestAnimationFrame(() => {
      exitShake.value = true;
      setTimeout(() => (exitShake.value = false), 400);
    });
  } else if (which === 'terms') {
    termsShake.value = false;
    requestAnimationFrame(() => {
      termsShake.value = true;
      setTimeout(() => (termsShake.value = false), 400);
    });
  }
};

const allRequiredAgreed = computed(() => agreedClause.value && agreedPersonal.value);

const allAgreed = computed(
  () => agreedClause.value && agreedPersonal.value && agreedMarketing.value,
);

const exchangedTokens = new Set();

const DEFAULT_PARTNER_ERROR_MESSAGE = '복지찬스 앱에서 다시 시도해주세요';

const normalizeErrorMessage = (value) => {
  if (!value) return '';
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(normalizeErrorMessage).filter(Boolean).join('\n');
  if (typeof value === 'object') {
    return (
      normalizeErrorMessage(value.message) ||
      normalizeErrorMessage(value.msg) ||
      normalizeErrorMessage(value.reason)
    );
  }
  return String(value);
};

const getPartnerErrorMessage = (error, fallback = DEFAULT_PARTNER_ERROR_MESSAGE) => {
  const data = error?.response?.data;
  return (
    normalizeErrorMessage(data?.detail) ||
    normalizeErrorMessage(data?.message) ||
    (!error?.response ? normalizeErrorMessage(error?.message) : '') ||
    fallback
  );
};

const showPartnerError = (title, message) => {
  errorTitle.value = title;
  errorMessage.value = message || DEFAULT_PARTNER_ERROR_MESSAGE;
  showTermsModal.value = false;
  showConsent.value = false;
  step.value = 'error';
};

const saveTokens = async (data) => {
  await store.dispatch('user/snslogin', {
    access_token: data.access_token,
    refresh_token: data.refresh_token,
    id: data.id,
    name: data.name,
  });
};

const sendPartnerAuthToApp = (data) => {
  sendMessageToApp({
    action: 'setPartnerAuth',
    payload: {
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresIn: data.expires_in,
      userId: data.id,
      name: data.name,
      partnerSource: 'smartwel',
    },
  });
};

const loadTermsContent = async () => {
  try {
    const result = await getTermsAndPoliciesAPI();
    const list = result?.content ?? [];
    clauseContent.value = list[1]?.contents ?? '';
    personalContent.value = list[2]?.contents ?? '';
    marketingContent.value = list[3]?.contents ?? '';
  } catch (e) {
    console.warn('[partner/smartwel] 약관 본문 로딩 실패 (무시):', e);
  }
};

const openConsent = (type) => {
  if (type === 'clause') {
    consentHeader.value = '서비스 이용약관';
    consentContent.value = clauseContent.value || '내용을 불러올 수 없습니다.';
  } else if (type === 'personal') {
    consentHeader.value = '개인정보 수집 및 이용동의';
    consentContent.value = personalContent.value || '내용을 불러올 수 없습니다.';
  } else if (type === 'marketing') {
    consentHeader.value = '마케팅 수신 동의';
    consentContent.value = marketingContent.value || '내용을 불러올 수 없습니다.';
  }
  showConsent.value = true;
};
const closeConsent = () => {
  showConsent.value = false;
};

const toggleAll = () => {
  const next = !allAgreed.value;
  agreedClause.value = next;
  agreedPersonal.value = next;
  agreedMarketing.value = next;
};

const onClickJoin = async () => {
  if (!allRequiredAgreed.value || submitting.value) return;

  const token = typeof route.query.token === 'string' ? route.query.token : null;
  if (!token) return;

  submitting.value = true;
  try {
    // 약관 동의 시점에 비로소 자동 가입 처리(autoSignUp:true)로 토큰 발급.
    // 진입 시 4xx 검사만 했고 토큰을 보유하지 않으므로 여기서 실제 가입을 트리거.
    const data = await AuthApi.partnerSmartwel(token, true);
    // TODO: 기존 로그인 세션의 로그아웃 처리 확인 필요
    await saveTokens(data);

    await userAgreed({ agreed: true }, 'clause');
    await userAgreed({ agreed: true }, 'personal');
    if (agreedMarketing.value) {
      await userAgreed({ agreed: true }, 'marketing');
    }

    // 약관 동의로 ROLE_USER가 부여된 상태에서 토큰 재발급.
    // partnerSmartwel 시점 토큰은 authorities가 비어있어 권한 검증이 필요한
    // 후속 요청(예: 앱의 FCM 디바이스 토큰 등록)이 거부되므로, 새 토큰으로 교체 후 앱에 전달.
    let dataForApp = data;
    try {
      const refreshed = await AuthApi.refresh(data.refresh_token);
      dataForApp = {
        ...data,
        access_token: refreshed.access_token,
        refresh_token: refreshed.refresh_token,
        expires_in: refreshed.expires_in,
      };
      await saveTokens(dataForApp);
    } catch (e) {
      console.warn(
        '[partner/smartwel] 동의 후 토큰 재발급 실패 (기존 토큰으로 진행):',
        e?.response?.data || e,
      );
    }
    sendPartnerAuthToApp(dataForApp);

    // 가입 직후 partner(복지P 잔액 등) 정보 갱신 → 구독 안내/결제 시트에서 잔액 정상 표시
    try {
      await myInfoStore.fetchMyInfo();
    } catch (err) {
      DEV_MODE && console.warn('[partner/smartwel] 가입 후 myInfo 갱신 실패:', err);
    }

    completeKind.value = 'join';
    showTermsModal.value = false;
    // 가입 완료 → 정기구독 안내(step 'done') 페이지로 전환. 진입 화면(prompt)은 언마운트됨
    step.value = 'done';
  } catch (e) {
    console.error('[partner/smartwel] 가입 실패:', e?.response?.data || e);
    showPartnerError(
      '가입에 실패했습니다',
      getPartnerErrorMessage(e, '가입 처리에 실패했습니다. 잠시 후 다시 시도해주세요.'),
    );
  } finally {
    submitting.value = false;
  }
};

const onClickClose = () => {
  showExitModal.value = true;
};
const closeExitModal = () => {
  showExitModal.value = false;
};
const confirmExit = () => {
  showExitModal.value = false;
  router.replace('/main');
};

const goHome = () => {
  router.replace('/main');
};

const showDebug = ref(true);
const hideDebug = () => {
  showDebug.value = false;
};

const goPointCharge = () => {
  window.history.replaceState(window.history.state, '', '/main');
  router.replace('/purchase/points');
};

/** 단계2 "구독 없이 포인트 전환" → 복지찬스 포인트 전환 페이지 */
const goPointExchange = () => {
  router.replace('/purchase/exchange');
};

/** 구독 화면 X 닫기: 메인으로 이동 (포인트 전환과 동일하게 partner URL을 main으로 치환) */
const closeSubscriptionScreen = () => {
  window.history.replaceState(window.history.state, '', '/main');
  router.replace('/main');
};

/** 구독 시작하기 버튼: 정기구독 안내(단계2)는 뒤에 유지한 채 결제 바텀시트를 위로 슬라이드업 */
const showPaymentSheet = ref(false);
const onStartSubscription = () => {
  showPaymentSheet.value = true;
};
/** 결제 시트 뒤로/닫기: 결제 시트만 닫으면 뒤의 정기구독 안내(step 'done')로 복귀 */
const closePaymentSheet = () => {
  showPaymentSheet.value = false;
};
/**
 * 결제 성공(@pay): 시트는 결제(createOrder)까지 완료한 상태.
 * → 시트/안내 닫고 "구독 시작" 토스트를 잠깐 보여준 뒤, 토스트가 닫힐 때 메인으로 이동.
 */
// 구독 완료 처리 — 내사주 유무로 분기
//  · 내사주 없음 → 사주 등록 유도 완료 팝업(SmartwelSubscribeCompleteSheet)
//  · 내사주 있음 → (TODO: v2.4 별도 디자인 적용 예정) 임시로 기존 토스트
const showComplete = ref(false);
const hasSaju = ref(false);
const showGuide = ref(false);

// 구독 안내(done)·결제·완료·가이드 등 오버레이가 떠 있으면 배경 스크롤 잠금
const isOverlayOpen = computed(
  () => step.value === 'done' || showPaymentSheet.value || showComplete.value || showGuide.value,
);
useScrollLock(isOverlayOpen);

const goMain = () => {
  window.history.replaceState(window.history.state, '', '/main');
  router.replace('/main');
};

const onPay = async () => {
  showPaymentSheet.value = false;
  await primarySajuStore.fetchMySaju();
  hasSaju.value = !!primarySajuStore.mySajuId;
  showComplete.value = true;
};

const onCompleteClose = () => {
  // 닫으면 메인으로 — 뒤에 깔린 구독 안내 화면(구독 신청하기 버튼)이 다시 보이지 않도록
  showComplete.value = false;
  goMain();
};
const onCompleteRegisterSaju = () => {
  showComplete.value = false;
  window.history.replaceState(window.history.state, '', '/main');
  router.replace('/mypage/moreinfo');
};
// 내사주 있음 — "지금 바로 이용하기" → 내 사주 분석 화면으로
const onCompleteUse = () => {
  showComplete.value = false;
  window.history.replaceState(window.history.state, '', '/main');
  router.replace(`/analysis/${primarySajuStore.mySajuId}`);
};
const onCompleteGuide = () => {
  showComplete.value = false;
  showGuide.value = true;
};
const onGuideClose = () => {
  // 뒤로 → 가이드를 연 완료 팝업으로 복귀
  showGuide.value = false;
  showComplete.value = true;
};
const onGuideRegisterSaju = () => {
  showGuide.value = false;
  window.history.replaceState(window.history.state, '', '/main');
  router.replace('/mypage/moreinfo');
};


/**
 * 진입 시 partnerSmartwel(autoSignUp:false) 1회 호출로 가입 여부 판정 후 분기.
 * - 200 (기존 사용자): 즉시 로그인 처리 (기존 세션 있으면 덮어쓰기)
 * - 404 (미가입): 로그인 상태에 따라 분기
 *   · 로그인 O → link-prompt (현재 계정에 연동할지 묻기)
 *   · 로그인 X → 약관 동의 화면
 * - 그 외 에러: 에러 화면
 *
 * 미가입 케이스에서 실제 가입 처리는 onClickJoin에서 partnerSmartwel(autoSignUp:true) 호출.
 */
const handleEntryToken = async (token) => {
  if (exchangedTokens.has(token)) return;
  exchangedTokens.add(token);
  try {
    const data = await AuthApi.partnerSmartwel(token, false);
    // 200: 기존 smartwel 사용자 → 즉시 토큰 저장 + 앱 동기화 + 메인 진입
    // TODO: 기존 로그인 세션의 로그아웃 처리 확인 필요
    await saveTokens(data);
    sendPartnerAuthToApp(data);
    router.replace('/main');
  } catch (e) {
    if (e?.response?.status === 404) {
      // 미가입 → 가입/연동 분기
      // - 로그인 O + autoLink=1 (login-prompt에서 로그인 거쳐 복귀): 자동으로 연동 API 호출
      // - 로그인 O: link-prompt (현재 계정에 연동 vs 새로 시작)
      // - 로그인 X: login-prompt (기존 계정으로 로그인해서 연동 vs 새로 시작)
      DEV_MODE &&
        console.log('[smartwel] 404 진입 분기:', {
          isLoggedIn: isLoggedIn.value,
          autoLinkQuery: route.query.autoLink,
          token_in_storage: !!sessionStorage.getItem('X-Token'),
          store_token: !!store.state.user?.token,
        });
      if (isLoggedIn.value && route.query.autoLink === '1') {
        await onClickLinkExisting();
        return;
      }
      step.value = 'prompt';
      return;
    }
    console.error('[partner/smartwel] 진입 실패:', e?.response?.data || e);
    showPartnerError('연결에 실패했습니다', getPartnerErrorMessage(e));
  }
};

/**
 * "기존 계정 연동하기" (비로그인): 로그인 트리거 + 로그인 성공 시 자동 연동.
 * - 앱 웹뷰: sendMessageToApp({action:'login'})으로 네이티브 로그인 호출.
 *   webview URL이 바뀌지 않으므로 isLoggedIn watcher로 변화 감지 → 자동 연동.
 * - 웹 브라우저: /auth/login?to=... 로 이동, 복귀 시 autoLink=1 query로 자동 연동.
 */
const onClickLoginExisting = () => {
  const token = typeof route.query.token === 'string' ? route.query.token : null;
  if (!token) return;
  autoLinkPending.value = true;
  if (isInRnWebView) {
    // 앱: partner 메타(로고 URL 등)와 함께 login 메시지 전송 → 앱이 native 로그인 화면에 듀얼 로고 표시
    const partner = getPartnerMeta('smartwel');
    sendMessageToApp({
      action: 'login',
      payload: {
        partnerSource: 'smartwel',
        partnerName: partner?.name,
        partnerLogoUrl: partner?.logoUrl,
        partnerLogoUrlInverted: partner?.logoUrlInverted,
      },
    });
    return;
  }
  const returnPath = `/partner/smartwel?token=${encodeURIComponent(token)}&autoLink=1`;
  router.replace({ path: '/auth/login', query: { to: returnPath } });
};

/** "연동하기": 기존 로그인 토큰을 그대로 둔 채 smartwel을 현재 계정에 연동 */
const onClickLinkExisting = async () => {
  if (linking.value) return;
  const token = typeof route.query.token === 'string' ? route.query.token : null;
  if (!token) return;
  linking.value = true;
  try {
    const result = await AuthApi.linkPartnerSmartwel(token);
    // linked=true(이번 연동) / alreadyLinked=true(이미 연동됨) 둘 다 성공 처리.
    // 간편가입 완료 시 띄우는 팝업과 동일한 완료 모달 노출.
    if (result?.linked || result?.alreadyLinked) {
      // 연동 직후 partner(복지P 잔액 등) 정보 갱신
      try {
        await myInfoStore.fetchMyInfo();
      } catch (err) {
        DEV_MODE && console.warn('[partner/smartwel] 연동 후 myInfo 갱신 실패:', err);
      }
      completeKind.value = 'link';
      // 연동 완료 → 정기구독 안내(step 'done') 페이지로 전환. 진입 화면(prompt)은 언마운트됨
      step.value = 'done';
    } else {
      showPartnerError('연동에 실패했습니다', '연동 결과를 확인할 수 없습니다.');
    }
  } catch (e) {
    console.error('[partner/smartwel] 연동 실패:', e?.response?.data || e);
    showPartnerError(
      '연동에 실패했습니다',
      getPartnerErrorMessage(e, '연동 처리에 실패했습니다. 잠시 후 다시 시도해주세요.'),
    );
  } finally {
    linking.value = false;
  }
};

/**
 * 메인 카드(낭만만세력 계정연동으로 시작) 클릭.
 * - 로그인 O: 현재 계정에 smartwel 연동
 * - 로그인 X: 기존 낭만 계정으로 로그인 트리거 후 자동 연동
 */
const onClickPrimaryCard = () => {
  if (isLoggedIn.value) {
    onClickLinkExisting();
  } else {
    onClickLoginExisting();
  }
};

/** 서브 카드(복지찬스 계정으로 시작) 클릭: 약관 모달 표시. 가입 처리는 onClickJoin에서 진행 */
const onClickSecondaryCard = () => {
  showTermsModal.value = true;
  loadTermsContent();
};

const closeTermsModal = () => {
  showTermsModal.value = false;
};

// 웹뷰에서 네이티브 로그인 후 isLoggedIn 변경되면 자동 연동 호출.
// (네이티브 로그인은 webview URL을 바꾸지 않으므로 mount-time autoLink query 검사로는 부족)
watch(isLoggedIn, async (loggedIn, prev) => {
  DEV_MODE &&
    console.log('[smartwel] isLoggedIn watcher:', {
      prev,
      next: loggedIn,
      autoLinkPending: autoLinkPending.value,
      step: step.value,
    });
  if (!loggedIn) return;
  if (!autoLinkPending.value) return;
  if (step.value !== 'prompt') return;
  autoLinkPending.value = false;
  await onClickLinkExisting();
});

// 추가 진단: store.state.user 변경 추적 (가입 후 토큰 반영 시점 확인)
watch(
  () => store.state.user?.token,
  (token) => {
    DEV_MODE &&
      console.log('[smartwel] store.user.token changed:', {
        hasToken: !!token,
        sessionXToken: !!sessionStorage.getItem('X-Token'),
        step: step.value,
        autoLinkQuery: route.query.autoLink,
      });
  },
);

onMounted(async () => {
  DEV_MODE &&
    console.log('[smartwel] mounted:', {
      token_query: route.query.token ? '(present)' : '(missing)',
      autoLink_query: route.query.autoLink,
      isLoggedIn: isLoggedIn.value,
      store_token: !!store.state.user?.token,
      sessionXToken: !!sessionStorage.getItem('X-Token'),
    });
  const isDev = import.meta.env.VITE_APP_MODE === 'development' || import.meta.env.DEV;
  // dev 환경 미리보기:
  // - ?preview=loading → 로딩 화면(단계 진행 차단)
  // - ?preview=error   → 에러 화면
  // - ?preview=prompt  → 카드 선택 화면
  // - ?preview=terms   → 카드 선택 + 약관 모달 열림
  if (isDev) {
    if (route.query.preview === 'loading') {
      step.value = 'loading';
      return;
    }
    if (route.query.preview === 'error') {
      showPartnerError('연결에 실패했습니다', 'preview error');
      return;
    }
    if (route.query.preview === 'prompt') {
      step.value = 'prompt';
      return;
    }
    if (route.query.preview === 'terms') {
      step.value = 'prompt';
      showTermsModal.value = true;
      loadTermsContent();
      return;
    }
    // ?preview=done → 정기구독 안내(단계2). variant=link 면 연동완료 카피
    if (route.query.preview === 'done') {
      completeKind.value = route.query.variant === 'link' ? 'link' : 'join';
      step.value = 'done';
      return;
    }
  }

  const token = typeof route.query.token === 'string' ? route.query.token : null;

  if (!token) {
    showPartnerError('연결에 실패했습니다', '잘못된 접근입니다.');
    return;
  }

  await handleEntryToken(token);
});
</script>

<template>
  <div class="partner-smartwel">
    <!-- 로딩/에러 공통: 듀얼 로고 브랜딩 -->
    <div v-if="step === 'loading' || step === 'error'" class="state-screen">
      <div class="state-logos">
        <img :src="nangmanLogo" alt="낭만만세력" class="state-logo state-logo--nangman" />
        <span class="state-logos-x" aria-hidden="true">×</span>
        <img :src="smartwelLogo" alt="복지찬스" class="state-logo state-logo--smartwel" />
      </div>

      <div v-if="step === 'loading'" class="state-loading">
        <p class="state-loading-text">로그인 중입니다...</p>
      </div>

      <div v-else class="state-error">
        <p class="state-error-title">{{ errorTitle }}</p>
        <p class="state-error-desc">{{ errorMessage || DEFAULT_PARTNER_ERROR_MESSAGE }}</p>
        <button type="button" class="state-error-btn" @click="goHome">홈으로 이동</button>
      </div>

      <!-- DEV 환경 한정 디버그 오버레이 (실제 UI에 영향 없도록 absolute) -->
      <div
        v-if="DEV_MODE && errorMessage && step === 'error' && showDebug"
        class="state-error-debug"
      >
        <button
          type="button"
          class="state-error-debug-close"
          aria-label="디버그 닫기"
          @click="hideDebug"
        >
          ×
        </button>
        <p class="state-error-debug-text">{{ errorMessage }}</p>
      </div>
    </div>

    <div v-else-if="step === 'prompt'" class="welcome-content">
      <button type="button" class="welcome-close" aria-label="닫기" @click="onClickClose">
        <img :src="closeIcon" alt="" class="welcome-close-icon" />
      </button>

      <div class="partner-badge">
        <span class="partner-badge-dot" aria-hidden="true"></span>
        <img :src="smartwelLogoWhite" alt="smartwel" class="partner-badge-logo" />
        <span>회원 전용</span>
      </div>

      <div class="welcome-hero">
        <div class="welcome-hero-title">
          <span class="highlight">낭만만세력</span>에<br />
          오신 것을 환영합니다
        </div>
      </div>

      <div class="main-copy">
        <div class="main-copy__title">
          복지포인트로 <span class="highlight">사주 서비스를 즐겨요</span>
        </div>
        <div class="main-copy__services">
          <span class="main-copy__service">오늘의 운세</span>
          <span class="main-copy__service">낭만궁합</span>
          <span class="main-copy__service">맞춤택일</span>
          <span class="main-copy__service">성향분석</span>
        </div>
      </div>

      <div class="connect-cta">
        <button
          type="button"
          class="choice-card choice-card--primary"
          :disabled="linking"
          @click="onClickPrimaryCard"
        >
          <div class="choice-card__header">
            <span class="choice-card__icon" aria-hidden="true">🎁</span>
            <span class="choice-card__title">낭만만세력 계정연동으로 시작</span>
          </div>
          <ul class="choice-card__features">
            <li>
              <span class="choice-card__check">✓</span>
              <span>보너스 <strong class="choice-card__bonus">500P</strong> 즉시 지급</span>
            </li>
            <li>
              <span class="choice-card__check">✓</span>
              <span>낭만만세력 앱에서 직접 로그인 가능</span>
            </li>
            <li>
              <span class="choice-card__check">✓</span>
              <span>SNS로 빠르고 간편한 연동</span>
            </li>
          </ul>
          <div class="choice-card__cta">
            <span class="choice-card__cta-left">
              <span>{{ linking ? '연동 중...' : '시작하기' }}</span>
              <span class="choice-card__badge">추천</span>
            </span>
            <span class="choice-card__arrow" aria-hidden="true">→</span>
          </div>
        </button>

        <button
          type="button"
          class="choice-card choice-card--secondary"
          @click="onClickSecondaryCard"
        >
          <div class="choice-card__header">
            <span class="choice-card__icon-sub" aria-hidden="true">📱</span>
            <span class="choice-card__title">복지찬스 계정으로 시작</span>
          </div>
          <ul class="choice-card__features">
            <li>
              <span class="choice-card__check">✓</span>
              <span>복지찬스 앱을 통해서만 이용 가능</span>
            </li>
            <li>
              <span class="choice-card__check">✓</span>
              <span>별도 연동 없이 바로 시작</span>
            </li>
            <li>
              <span class="choice-card__check">✓</span>
              <span class="choice-card__feature-wrap">
                마이페이지에서 추후 연동 가능
                <span class="choice-card__feature-sub">(연동 시 500P 지급)</span>
              </span>
            </li>
          </ul>
          <div class="choice-card__cta">
            <span>시작하기</span>
            <span class="choice-card__arrow" aria-hidden="true">→</span>
          </div>
        </button>

        <div class="connect-cta__tip">💡 복지포인트는 낭만 포인트로 전환해서 사용해요</div>
      </div>
    </div>

    <Consent
      v-if="showConsent"
      :header="consentHeader"
      :message="consentContent"
      button-text="확인"
      @close="closeConsent"
      @confirm="closeConsent"
    />

    <!-- 약관 동의 모달: '복지찬스 계정으로 시작' 클릭 시 -->
    <Transition name="modal">
      <div v-if="showTermsModal" class="modal-overlay" @click.self="triggerShake('terms')">
        <div class="terms-modal" :class="{ shake: termsShake }">
          <div class="terms-modal__header">
            <span class="terms-modal__title">이용 약관 동의</span>
            <button
              type="button"
              class="terms-modal__close"
              aria-label="닫기"
              @click="closeTermsModal"
            >
              ✕
            </button>
          </div>

          <div class="terms-area">
            <div class="terms-all" @click="toggleAll">
              <div class="terms-checkbox" :class="{ checked: allAgreed }">
                <span v-if="allAgreed">✓</span>
              </div>
              <span class="terms-all-label">전체 약관에 동의합니다.</span>
            </div>

            <div class="terms-list">
              <div class="terms-item" @click="agreedClause = !agreedClause">
                <div class="terms-checkbox" :class="{ checked: agreedClause }">
                  <span v-if="agreedClause">✓</span>
                </div>
                <span class="terms-label">
                  <span class="required">[필수]</span> 서비스 이용약관
                </span>
                <span class="terms-arrow" @click.stop="openConsent('clause')">보기 ›</span>
              </div>
              <div class="terms-item" @click="agreedPersonal = !agreedPersonal">
                <div class="terms-checkbox" :class="{ checked: agreedPersonal }">
                  <span v-if="agreedPersonal">✓</span>
                </div>
                <span class="terms-label">
                  <span class="required">[필수]</span> 개인정보 수집·이용
                </span>
                <span class="terms-arrow" @click.stop="openConsent('personal')">보기 ›</span>
              </div>
              <div
                class="terms-item terms-item-marketing"
                @click="agreedMarketing = !agreedMarketing"
              >
                <div class="terms-checkbox" :class="{ checked: agreedMarketing }">
                  <span v-if="agreedMarketing">✓</span>
                </div>
                <div class="terms-label-wrap">
                  <span class="terms-label">
                    <span class="optional">[선택]</span> 이벤트·혜택 알림 받기
                  </span>
                  <span class="terms-sublabel">놓치면 아쉬운 할인과 신규 서비스 소식</span>
                </div>
                <span class="terms-arrow" @click.stop="openConsent('marketing')">보기 ›</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            class="join-btn"
            :class="{ active: allRequiredAgreed }"
            :disabled="!allRequiredAgreed || submitting"
            @click="onClickJoin"
          >
            {{ submitting ? '처리 중...' : '동의하고 시작하기' }}
          </button>
        </div>
      </div>
    </Transition>

    <!-- 가입/연동 완료(step 'done') → 단계2 풀스크린 (정기구독 제안) -->
    <SmartwelSubscriptionScreen
      v-if="step === 'done'"
      :variant="completeKind"
      @close="closeSubscriptionScreen"
      @start="onStartSubscription"
      @exchange="goPointExchange"
    />

    <!-- 구독 시작하기 → 시연 단계3 결제 바텀시트 (단계2 위에 슬라이드 업) -->
    <SmartwelPaymentSheet v-if="showPaymentSheet" @close="closePaymentSheet" @pay="onPay" />

    <!-- 구독 완료 팝업 (내사주 유무 분기) -->
    <SmartwelSubscribeCompleteSheet
      v-if="showComplete"
      :has-saju="hasSaju"
      @close="onCompleteClose"
      @use="onCompleteUse"
      @register-saju="onCompleteRegisterSaju"
      @guide="onCompleteGuide"
    />

    <!-- 이용 안내(가이드) 화면 -->
    <SmartwelGuideScreen
      v-if="showGuide"
      @close="onGuideClose"
      @register-saju="onGuideRegisterSaju"
    />

    <!-- 나가기 확인 팝업 -->
    <Transition name="modal">
      <div v-if="showExitModal" class="modal-overlay" @click.self="triggerShake('exit')">
        <div class="exit-modal" :class="{ shake: exitShake }">
          <div class="exit-modal-content">
            <div class="exit-modal-title">나중에 하시겠어요?</div>
            <div class="exit-modal-desc">
              약관에 동의하셔야<br />
              서비스를 이용하실 수 있어요
            </div>
          </div>
          <div class="exit-modal-actions">
            <button type="button" class="exit-modal-btn secondary" @click="confirmExit">
              홈으로 이동
            </button>
            <button type="button" class="exit-modal-btn primary" @click="closeExitModal">
              계속 보기
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style lang="scss" scoped>
// 컬러
$navy-blue: #1e3a5f;

$orange300: #eb4c10; // 기본 오렌지
$orange200: #ff8800; // 옅은 오렌지
$orange100: #fff8f5; // 아주 밝은 오렌지

$orange: $orange300;
$orange-dark: #b84818;
$indigo: #3d5a80;

$text-main: #333333; // gray800
$text-sub: #333; // gray700

$bg: #f8f8f8;

.partner-smartwel {
  width: 100%;
  min-height: calc(100vh - var(--safe-area-top));
  background: linear-gradient(180deg, $orange100 0%, #fff 60%);
  color: #2c2c2a;
  font-family:
    'Pretendard Variable',
    Pretendard,
    -apple-system,
    BlinkMacSystemFont,
    'Apple SD Gothic Neo',
    'Noto Sans KR',
    sans-serif;
  letter-spacing: -0.3px;
  word-break: keep-all;
  -webkit-font-smoothing: antialiased;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

/* 로딩/에러 공통 화면 */
.state-screen {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: calc(40px + var(--safe-area-top, 0px)) 24px calc(32px + var(--safe-area-bottom, 0px));
  gap: 36px;

  @media (max-width: 768px) {
    padding: calc(24px + var(--safe-area-top, 0px)) 16px calc(24px + var(--safe-area-bottom, 0px));
  }
}

.state-logos {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}

.state-logo {
  display: block;
  width: auto;
}

.state-logo--nangman {
  height: 36px;
}

.state-logo--smartwel {
  height: 24px;
}

.state-logos-x {
  font-size: 16px;
  color: #c5bdb0;
  font-weight: 300;
  line-height: 1;
}

.state-loading-text {
  font-size: 15px;
  color: $text-main;
  margin: 0;
  letter-spacing: -0.3px;
}

.state-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 320px;
}

.state-error-title {
  font-size: 18px;
  font-weight: 700;
  color: $text-main;
  margin: 0;
  letter-spacing: -0.4px;
}

.state-error-desc {
  font-size: 14px;
  color: $text-sub;
  margin: 8px 0 0;
  line-height: 1.55;
  letter-spacing: -0.3px;
  white-space: pre-line;
  overflow-wrap: anywhere;
}

/* DEV 디버그 오버레이 — 화면 하단 고정, UI 차지 X */
.state-error-debug {
  position: fixed;
  left: 12px;
  right: 12px;
  bottom: calc(12px + var(--safe-area-bottom, 0px));
  padding: 10px 36px 10px 12px;
  background: rgba(40, 40, 40, 0.92);
  border-radius: 8px;
  z-index: 100;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.state-error-debug-text {
  margin: 0;
  font-size: 11px;
  color: #fff;
  font-family: 'SF Mono', Menlo, Consolas, monospace;
  word-break: break-all;
  line-height: 1.4;
  text-align: left;
}

.state-error-debug-close {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  font-family: inherit;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
}

.state-error-btn {
  margin-top: 28px;
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, $orange 0%, $orange-dark 100%);
  color: white;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.3px;
  cursor: pointer;
  font-family: inherit;
  box-shadow: 0 4px 16px rgba(216, 90, 48, 0.25);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.welcome-content {
  position: relative;
  max-width: 480px;
  margin: 0 auto;
  padding: calc(14px + var(--safe-area-top, 0px)) 24px calc(32px + var(--safe-area-bottom, 0px));
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  @media (max-width: 768px) {
    padding: calc(24px + var(--safe-area-top, 0px)) 16px calc(24px + var(--safe-area-bottom, 0px));
  }
}

.welcome-close {
  position: fixed;
  top: calc(8px + var(--safe-area-top, 0px));
  right: 16px;
  z-index: 10;
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.welcome-close-icon {
  width: 24px;
  height: 24px;
  display: block;
}

.partner-badge {
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  background: $navy-blue;
  border: 1.5px solid $navy-blue;
  padding: 6px 14px;
  border-radius: 100px;
  font-size: 13px;
  font-weight: 800;
  line-height: 1;
  color: #fff;
  align-self: center;
  margin-top: 2px;
  letter-spacing: -0.2px;
  box-shadow: 0 2px 8px rgba(30, 58, 95, 0.25);

  > span {
    display: inline-flex;
    align-items: center;
    line-height: 1;
  }

  > span:not(.partner-badge-dot) {
    padding-top: 1px;
  }
}

.partner-badge-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: $orange200;
}

.partner-badge-logo {
  height: 14px;
  width: auto;
  display: block;
}

.partner-badge-divider {
  width: 1px;
  height: 10px;
  background: rgba(216, 90, 48, 0.35);
}

.welcome-hero {
  text-align: center;
  margin: 22px 0 18px;
}

.welcome-hero-title {
  font-size: 22px;
  font-weight: 800;
  color: $text-main;
  line-height: 1.4;
  letter-spacing: -0.6px;

  .highlight {
    color: #eb4c10;
  }
}

.main-copy {
  text-align: center;
  margin-bottom: 4px;
  padding: 14px 16px;
  background: $orange100;
  border-radius: 12px;

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $text-main;
    line-height: 1.5;
    letter-spacing: -0.4px;
    margin-bottom: 10px;

    .highlight {
      color: $orange;
    }
  }

  &__services {
    display: inline-flex;
    flex-wrap: wrap;
    justify-content: center;
    font-size: 14px;
    font-weight: 500;
    color: #8c6a3e;
    letter-spacing: -0.2px;
    padding: 0 8px;
    line-height: 1.7;
    opacity: 0.85;
  }

  &__service {
    &::after {
      content: '·';
      color: #c5bdb0;
      margin: 0 4px;
    }

    &:last-child::after {
      content: ' ...';
      color: #c5bdb0;
    }
  }
}

.connect-cta {
  margin-top: 24px;
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__tip {
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: $orange300;
    background: $orange100;
    border: 1px solid #ffd4a8;
    border-radius: 10px;
    margin-top: 12px;
    padding: 11px 14px;
    letter-spacing: -0.2px;
    line-height: 1.5;
  }
}

.choice-card {
  width: 100%;
  display: block;
  border-radius: 16px;
  font-family: inherit;
  letter-spacing: -0.3px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 1.5px solid transparent;
  padding: 20px 20px 16px;
  text-align: left;
  position: relative;
  overflow: hidden;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 16px;
  }

  &__icon,
  &__icon-sub {
    font-size: 24px;
    line-height: 1;
    flex-shrink: 0;
  }

  &__icon-sub {
    font-size: 22px;
    opacity: 0.7;
  }

  &__title {
    font-size: 16px;
    font-weight: 800;
    line-height: 1.4;
    flex: 1;
    min-width: 0;
    letter-spacing: -0.4px;
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;

    li {
      font-size: 13px;
      font-weight: 500;
      display: flex;
      align-items: center;
      gap: 8px;
      line-height: 1.5;
    }

    strong {
      font-weight: 800;
    }
  }

  &__check {
    font-size: 11px;
    font-weight: 800;
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &__bonus {
    font-size: 17px;
    font-weight: 900;
    letter-spacing: -0.3px;
    margin: 0 1px;
  }

  &__cta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 12px;
    border-top: 1px solid;
    font-size: 14px;
    font-weight: 700;
  }

  &__cta-left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    background: #ffa840;
    color: white;
    font-size: 12px;
    font-weight: 800;
    line-height: 1.2;
    padding: 3px 9px;
    border-radius: 100px;
    letter-spacing: 0.3px;
    box-shadow: 0 2px 6px rgba(255, 168, 64, 0.4);
  }

  &__arrow {
    font-size: 16px;
    font-weight: 800;
    transition: transform 0.18s ease;
  }

  &:hover &__arrow {
    transform: translateX(4px);
  }

  &__feature-wrap {
    display: flex;
    flex-direction: column;
  }

  &__feature-sub {
    font-size: 13px;
    color: #a8a29a;
    font-weight: 500;
    letter-spacing: -0.2px;
  }

  &--primary {
    background: linear-gradient(160deg, $navy-blue 0%, #2d4f7f 100%);
    border-color: $navy-blue;
    color: white;
    box-shadow: 0 8px 24px rgba(30, 58, 95, 0.25);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 12px 32px rgba(30, 58, 95, 0.35);
    }

    .choice-card__features li {
      color: rgba(255, 255, 255, 0.92);
      font-size: 14px;
    }

    .choice-card__check {
      color: white;
      background: rgba(255, 168, 64, 0.9);
    }

    .choice-card__bonus {
      color: #ffd78a;
    }

    .choice-card__cta {
      border-top-color: rgba(255, 255, 255, 0.15);
      color: white;
    }
  }

  &--secondary {
    background: #fafaf8;
    border-color: #e0ddd8;
    color: #555;
    padding: 16px 18px 14px;

    &:hover {
      border-color: #999;
      background: #f5f3ef;
    }

    .choice-card__header {
      margin-bottom: 12px;
      gap: 8px;
    }

    .choice-card__icon-sub {
      font-size: 20px;
    }

    .choice-card__title {
      font-size: 15px;
      color: $text-main;
    }

    .choice-card__features {
      gap: 6px;
      margin-bottom: 12px;

      li {
        font-size: 14px;
        color: #666;
        gap: 7px;
      }
    }

    .choice-card__check {
      width: 16px;
      height: 16px;
      font-size: 10px;
      color: white;
      background: #bbb;
    }

    .choice-card__cta {
      border-top-color: #eee;
      color: #555;
      padding-top: 8px;
      font-size: 14px;
    }
  }
}

.terms-area {
  background: #f8f8f8;
  border-radius: 12px;
  padding: 13px 16px;
}

.terms-all {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ece8e0;
  margin-bottom: 10px;
  cursor: pointer;
}

.terms-all-label {
  font-size: 14px;
  font-weight: 700;
  color: $text-main;
}

.terms-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.terms-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.terms-item-marketing {
  align-items: flex-start;
  padding-top: 2px;

  .terms-checkbox {
    margin-top: 1px;
  }

  .terms-arrow {
    margin-top: 1px;
  }
}

.terms-checkbox {
  width: 19px;
  height: 19px;
  border: 1.5px solid #c5bdb0;
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: white;
  font-size: 12px;
  font-weight: 700;
  color: white;

  &.checked {
    background: $orange;
    border-color: $orange;
  }
}

.terms-label {
  font-size: 14px;
  color: #555;
  flex: 1;
  letter-spacing: -0.2px;

  .required {
    margin: 0;
    color: $orange;
    font-weight: 600;
  }

  .optional {
    color: #999;
  }
}

.terms-label-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;

  .terms-label {
    flex: none;
  }
}

.terms-sublabel {
  font-size: 12px;
  color: #999;
  font-weight: 500;
  letter-spacing: -0.2px;
  line-height: 1.4;
}

.terms-arrow {
  font-size: 12px;
  color: #b5ac9c;
  cursor: pointer;
  padding: 4px 6px;
  margin-right: -4px;

  &:hover {
    color: $orange;
  }
}

.bottom-buttons {
  display: flex;
  flex-direction: column;
  padding-top: 6px;
  gap: 8px;
}

.join-btn {
  width: 100%;
  height: 54px;
  border: none;
  border-radius: 14px;
  background: #ececec;
  color: #555;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.3px;
  cursor: not-allowed;
  font-family: inherit;
  transition: all 0.2s ease;

  &.active {
    background: linear-gradient(135deg, $orange 0%, $orange-dark 100%);
    color: white;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(216, 90, 48, 0.25);
  }

  &:disabled {
    opacity: 1;
  }
}

/* 모달 공통 */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

/* 모달 등장/퇴장 애니메이션 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.2s ease;

  .complete-modal,
  .exit-modal,
  .terms-modal {
    transition:
      transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1),
      opacity 0.2s ease;
  }
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;

  .complete-modal,
  .exit-modal,
  .terms-modal {
    opacity: 0;
    transform: scale(0.85) translateY(10px);
  }
}

/* 바깥 클릭 시 위로 튀어오르는 느낌 */
@keyframes modal-bounce {
  0% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
  100% {
    transform: translateY(0);
  }
}

.complete-modal.shake,
.exit-modal.shake,
.terms-modal.shake {
  animation: modal-bounce 0.25s ease-out;
}

/* 완료 팝업 */
.complete-modal {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 320px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.complete-modal-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px;
  background: linear-gradient(135deg, $orange 0%, $orange-dark 100%);
  color: white;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.3px;
}

.complete-modal-icon {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: white;
  color: $orange;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  flex-shrink: 0;
}

.complete-modal-actions {
  display: flex;
  flex-direction: column;
}

.complete-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: white;
  border: none;
  border-top: 1px solid #f0ede8;
  font-size: 14.5px;
  font-weight: 600;
  color: $text-main;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: -0.3px;
  transition: background 0.15s;
  text-align: left;

  &:first-child {
    border-top: none;
  }

  &:hover {
    background: #fff5ec;
    color: $orange-dark;
  }

  &.primary {
    background: #fffaf3;
    color: $orange-dark;
    font-weight: 700;
  }
}

.complete-action-arrow {
  font-size: 18px;
  font-weight: 700;
  color: $orange;
}

/* 나가기 팝업 */
.exit-modal {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 280px;
  overflow: hidden;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}

.exit-modal-content {
  padding: 28px 22px 18px;
  text-align: center;
}

.exit-modal-title {
  font-size: 17px;
  font-weight: 700;
  color: $text-main;
  letter-spacing: -0.4px;
  margin-bottom: 8px;
  line-height: 1.4;
}

.exit-modal-desc {
  font-size: 13px;
  color: $text-main;
  line-height: 1.6;
  font-weight: 500;
  letter-spacing: -0.2px;
}

.exit-modal-actions {
  display: flex;
  border-top: 1px solid #f0ede8;
}

.exit-modal-btn {
  flex: 1;
  padding: 16px;
  border: none;
  background: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  letter-spacing: -0.3px;

  &.secondary {
    color: #888;
    border-right: 1px solid #f0ede8;

    &:hover {
      background: #f9f7f2;
    }
  }

  &.primary {
    color: $orange;
    font-weight: 700;

    &:hover {
      background: #fff5ec;
    }
  }
}

/* 약관 동의 모달 */
.terms-modal {
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 360px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__title {
    font-size: 16px;
    font-weight: 700;
    color: $text-main;
    letter-spacing: -0.3px;
  }

  &__close {
    border: none;
    background: transparent;
    font-size: 20px;
    color: #888;
    cursor: pointer;
    padding: 4px 8px;
    line-height: 1;

    &:hover {
      color: $text-main;
    }
  }
}
</style>
